import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import {Box, Form as GrommetForm, Grommet, ResponsiveContext, Text, ThemeContext} from "grommet";
import Section from "./Section";
import {sentencecase, snakeCase} from "../utils/string";
import Loading from "./Loading";
import {
    IconPencil as IconEdit
} from '@tabler/icons-react'
import Icon from "./Icon";
import Button from "./Button";
import {deepMerge} from "grommet/utils";
import KeyboardShortcut from "./KeyboardShortcut";

const ViewOnlyDefault = ({ value, ...rest }) => (
    <Box>
        <Text {...rest}>{value}</Text>
    </Box>
);

// Component that applies transparent border to a FormField using the parent's theme
const WrappedFormField = ({ element, themeMode }) => {
    const parentTheme = useContext(ThemeContext);
    const transparentTheme = deepMerge(parentTheme || {}, {
        formField: {
            border: {
                color: 'transparent',
                side: 'all',
            },
            content: {
                pad: 'none',
            },
        },
    });

    const ViewOnlyComponent = element.props.viewOnlyComponent || ViewOnlyDefault;

    return (
        <Grommet theme={transparentTheme} themeMode={themeMode}>
            {React.cloneElement(
                element,
                {
                    ...element.props,
                    component: ViewOnlyComponent,
                },
                element.props.children
            )}
        </Grommet>
    );
};

/*
    Deliberately not a hook. This runs once per child through React.Children.map and
    recurses into Box and Grid children, so the useContext this used to call meant the
    form's hook count tracked the shape of its children - and dropped to zero the moment
    `editable` flipped and drawComponent stopped being called at all. The theme is read
    once by the form instead and handed down.
*/
export const drawComponent = (element, themeMode) => {
    const displayName = element?.type?.displayName || element?.type?.name;

    if (displayName === 'FormField') {
        return <WrappedFormField element={element} themeMode={themeMode} />;
    }

    if (displayName === 'Box' || displayName === 'Grid') {
        return React.cloneElement(
            element,
            {},
            React.Children.map(element.props.children, (child) => drawComponent(child, themeMode))
        );
    }

    return element;
};

const displayComponents = (editable, children, themeMode) => {
    if (editable) {
        return children;
    }
    return React.Children.map(children, (child) => drawComponent(child, themeMode))
};

const StreamlinedForm = ({
    title,
    children,
    onSubmit,
    onReset,
    sectionProps,
    editOnly = false,
    hideFormButtons = false,
    otherActions,
    leftActions, // Actions that appear on the left side of Cancel button
    submitText="Save",
    icon,
    asBox,
    isCtaLoading,
    loggingComponentName,
    ...rest
}) => {
    const [editable, setEditable] = useState(editOnly);
    const [initialValue, setInitialValue] = useState(rest.value);
    const size = useContext(ResponsiveContext);
    // Save and Cancel are small on desktop, medium on a phone where buttons are 44px app-wide
    const buttonSize = size === "small" ? "medium" : "small";
    const themeContext = useContext(ThemeContext);
    const themeMode = themeContext?.dark ? 'dark' : 'light';
    const formRef = useRef(null);

    // Keyboard shortcut Cmd+Enter (Mac) or Ctrl+Enter (Windows) to submit form
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Check for Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux)
            const isModifierPressed = e.metaKey || e.ctrlKey;

            if (isModifierPressed && e.key === 'Enter' && editable && !isCtaLoading) {
                e.preventDefault();
                // Use requestSubmit to trigger form validation and submission
                const form = formRef.current?.querySelector('form');
                if (form) {
                    form.requestSubmit();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [editable, isCtaLoading]);

    const componentsToShow = displayComponents(editable, children, themeMode);
    const WrapperComponent = asBox ? Box : Section;

    return (
        <WrapperComponent
            {...sectionProps}
            { ...(title ? { title } : {}) }
            control={(
                <Box direction="row">
                    {!editable && otherActions}
                    {!editable &&
                        <Button
                            event={{section: `${loggingComponentName || snakeCase(title)}_form`, element: 'edit'}}
                            onClick={() => { setEditable(!editable); }}
                            icon={<Icon SelectedIcon={IconEdit} size="small" />}
                            round
                            hoverIndicator
                            tip={sentencecase(`Edit ${title}`)} />}
                </Box>
            )}
            icon={icon}
        >
            <div ref={formRef}>
                <GrommetForm
                    {...rest}
                    onSubmit={(event) => {
                        onSubmit && onSubmit(event);
                        setInitialValue(event.value);
                        !editOnly && setEditable(!editable);
                    }}
                >
                    {componentsToShow}
                    {!hideFormButtons && editable
                    && (
                        <Box margin={{ top: 'medium', bottom: 'small' }} direction="row-reverse" gap="medium">
                            <Box>
                                <Button
                                    event={{section: `${loggingComponentName || snakeCase(title)}_form`, element: 'submit'}}
                                    size={buttonSize}
                                    type="submit"
                                    primary
                                    label={(
                                        <Box direction="row" align="center">
                                            {isCtaLoading ?
                                                (<Loading plain/>) :
                                                (<>
                                                    <Text>{submitText}</Text>
                                                    <Box margin={{ left: 'xsmall' }}>
                                                        <KeyboardShortcut keys={['Cmd', 'Enter']} variant="primary" />
                                                    </Box>
                                                </>)}
                                        </Box>
                                    )}
                                    disabled={isCtaLoading}
                                />
                            </Box>
                            { onReset && (
                                <Box>
                                    <Button
                                        event={{section: `${loggingComponentName || snakeCase(title)}_form`, element: 'cancel'}}
                                        size={buttonSize}
                                        onClick={(event) => {
                                            event.initialValue = initialValue;
                                            onReset && onReset(event);
                                            !editOnly && setEditable(!editable);
                                        }}
                                        type="reset"
                                        label={(
                                            <Box width={{ min: 'xxsmall' }}>
                                                <Text>Cancel</Text>
                                            </Box>
                                        )}
                                    />
                                </Box>
                            )}
                            {leftActions}
                        </Box>
                    )}
                </GrommetForm>
            </div>
        </WrapperComponent>
    );
};

export default StreamlinedForm;
